import { TaxAndFeeSetting } from "../types/admin";

/** Helpers for calculating checkout-related taxes and fees. */

/** Round floating-point currency math to 2 decimals. */
export const roundCurrency = (amount: number): number =>
  Math.round((amount + Number.EPSILON) * 100) / 100;

/**
 * Find the matching tax and fee setting based on amount.
 * Returns the setting where amount falls within min-max range.
 * If the amount exceeds all configured tiers, the highest tier is used
 * so that large orders are never silently assigned a 0 fee.
 */
export const findMatchingTaxAndFeeSetting = (
  amount: number,
  settings: TaxAndFeeSetting[]
): TaxAndFeeSetting | null => {
  if (settings.length === 0) return null;

  const sortedSettings = [...settings].sort((a, b) => a.min - b.min);

  const matching = sortedSettings.find(
    (setting) => amount >= setting.min && amount <= setting.max
  );

  if (matching) return matching;

  // Amount exceeds the top tier max — fall back to the highest tier rather
  // than returning null (which would silently produce a $0 fee).
  const highest = sortedSettings[sortedSettings.length - 1];
  if (amount > highest.max) return highest;

  return null;
};

/**
 * Calculate tax based on amount and tax/fee settings
 */
export const calculateTax = (
  amount: number,
  settings: TaxAndFeeSetting[]
): number => {
  const matching = findMatchingTaxAndFeeSetting(amount, settings);
  if (!matching || matching.tax === null) {
    return 0;
  }
  return roundCurrency(amount * (matching.tax / 100));
};

/**
 * Calculate service fee based on amount and tax/fee settings
 */
export const calculateServiceFee = (
  amount: number,
  settings: TaxAndFeeSetting[]
): number => {
  const matching = findMatchingTaxAndFeeSetting(amount, settings);
  if (!matching || matching.fees === null) {
    return 0;
  }
  return roundCurrency(amount * (matching.fees / 100));
};
